// Cloud storage connections (Google Drive/OneDrive/Box/Dropbox) — wraps the
// same GraphqlAPI.cloudStorages sub-object (src/core/cloud-storages-api.js)
// the legacy app uses, since that's shared backend-contract code, not a view.
import { GraphqlAPI } from "../../core/graphql-api";
import { CLOUD_STORAGE } from "../../core/constants.js";
import GoogleOAuth2Client from "./oauth/googleOAuth2Client.js";
import MSOAuth2Client from "./oauth/msOAuth2Client.js";
import BoxOAuth2Client from "./oauth/boxOAuth2Client.js";
import DropboxOAuth2Client from "./oauth/dropboxOAuth2Client.js";

const graphqlServer = import.meta.env.VITE_GRAPHQL_SERVER_V1;

const OAUTH_CLIENTS = {
  [CLOUD_STORAGE.GOOGLE_DRIVE]: GoogleOAuth2Client,
  [CLOUD_STORAGE.ONE_DRIVE]: MSOAuth2Client,
  [CLOUD_STORAGE.BOX_DRIVE]: BoxOAuth2Client,
  [CLOUD_STORAGE.DROPBOX_DRIVE]: DropboxOAuth2Client,
};

export const AUTHENTICATION_STATUS = {
  AUTHENTICATED: "AUTHENTICATED",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
};

export default class CloudStorageService {
  constructor() {
    this.accessToken = localStorage.getItem("accessToken");
    this.graphqlAPIService = new GraphqlAPI(graphqlServer, this.accessToken);
  }

  async getAll() {
    return (await this.graphqlAPIService.cloudStorages.getAll()) ?? [];
  }

  async authorize({ id, name, provider, accessToken, refreshToken }) {
    return this.graphqlAPIService.cloudStorages.authorizeStorage(
      id, new Date().toISOString(), name, provider, accessToken, refreshToken
    );
  }

  async rename(storageId, name) {
    return this.graphqlAPIService.cloudStorages.updateStorage(storageId, name);
  }

  async remove(storageId) {
    return this.graphqlAPIService.cloudStorages.deleteStorage(storageId);
  }

  async uploadDocuments(projectId, storageId, cloudStorageFileIds, documentIds) {
    return this.graphqlAPIService.cloudStorages.uploadDocument(projectId, storageId, cloudStorageFileIds, documentIds);
  }

  // Returns an async generator of item-chunks for one folder, paging through
  // the connection automatically (11 items/page, matching legacy). Callers
  // create one generator per folder and call .next() again to load more
  // (e.g. on scroll), same shape as the old service-worker-proxy version.
  folderItems(storageId, parentFolderId) {
    const api = this.graphqlAPIService;
    return (async function* () {
      let pageCursor = null;
      let hasNextPage = false;
      do {
        const { items: { edges, pageInfo } } = await api.cloudStorages.getStorageItems(storageId, 11, pageCursor, parentFolderId);
        hasNextPage = pageInfo.hasNextPage;
        pageCursor = pageInfo.endCursor;
        yield edges.map((edge) => edge.node);
      } while (hasNextPage);
    })();
  }

  // Dispatches to the right provider's OAuth class and starts the redirect
  // flow. `existingStorage` (when re-authenticating an expired connection)
  // carries its id/name through so the callback page updates it in place
  // instead of creating a new connection.
  connect(provider, existingStorage = null) {
    const OAuthClient = OAUTH_CLIENTS[provider];
    if (!OAuthClient) throw new Error(`Unknown cloud storage provider: ${provider}`);
    const transitedState = {
      path: location.pathname,
      provider,
      existsStorage: existingStorage ? { id: existingStorage.id, name: existingStorage.name } : null,
    };
    return new OAuthClient().getAuthWithRedirect(transitedState);
  }
}
